import { useState } from 'react'
import { router } from '@inertiajs/react'
import { slugify } from '@/lib/utils'
import api from '@/lib/axios'

interface FormErrors {
  [key: string]: string
}

export function usePostsNewVM() {
  const [title, setTitle] = useState('')
  const [slug, setSlug] = useState('')
  const [excerpt, setExcerpt] = useState('')
  const [content, setContent] = useState('')
  const [status, setStatus] = useState('draft')
  const [visibility, setVisibility] = useState('public')
  const [categoryId, setCategoryId] = useState('')
  const [selectedTagIds, setSelectedTagIds] = useState<number[]>([])
  const [coverImage, setCoverImage] = useState<File | null>(null)
  const [coverPreview, setCoverPreview] = useState<string | null>(null)
  const [coverUrl, setCoverUrl] = useState<string | null>(null)
  const [coverBlobKey, setCoverBlobKey] = useState<string | null>(null)
  const [metaTitle, setMetaTitle] = useState('')
  const [metaDescription, setMetaDescription] = useState('')
  const [publishedAt, setPublishedAt] = useState(() => {
    const now = new Date()
    now.setHours(0, 0, 0, 0)
    now.setMinutes(now.getMinutes() - now.getTimezoneOffset())
    return now.toISOString().slice(0, 16)
  })
  const [sticky, setSticky] = useState(false)
  const [saving, setSaving] = useState(false)
  const [errors, setErrors] = useState<FormErrors>({})
  const [mediaDialogOpen, setMediaDialogOpen] = useState(false)

  const handleTitleChange = (val: string) => {
    setTitle(val)
    if (!slug || slug === slugify(title)) {
      setSlug(slugify(val))
    }
  }

  const handleTagsChange = (ids: number[]) => {
    setSelectedTagIds(ids)
  }

  const handleCoverChange = (file: File | null) => {
    setCoverImage(file)
    setCoverUrl(null)
    setCoverBlobKey(null)
    if (file) {
      const reader = new FileReader()
      reader.onload = (e) => setCoverPreview(e.target?.result as string)
      reader.readAsDataURL(file)
    } else {
      setCoverPreview(null)
    }
  }

  const handleMediaSelect = (url: string, blobKey: string) => {
    setCoverUrl(url)
    setCoverBlobKey(blobKey)
    setCoverPreview(url)
    setCoverImage(null)
  }

  const removeCover = () => {
    setCoverImage(null)
    setCoverPreview(null)
    setCoverUrl(null)
    setCoverBlobKey(null)
  }

  const handleCancel = () => {
    router.visit('/admin/posts')
  }

  const handleSubmit = async (submitStatus: string) => {
    setSaving(true)
    setErrors({})

    const formData = new FormData()
    formData.append('title', title)
    formData.append('slug', slug)
    formData.append('excerpt', excerpt)
    formData.append('content', content)
    formData.append('status', submitStatus)
    formData.append('visibility', visibility)
    if (categoryId) formData.append('category_id', categoryId)
    if (publishedAt) formData.append('published_at', publishedAt)
    if (metaTitle) formData.append('meta_title', metaTitle)
    if (metaDescription) formData.append('meta_description', metaDescription)
    if (sticky) formData.append('sticky', 'true')
    selectedTagIds.forEach((id) => formData.append('tag_ids[]', String(id)))
    if (coverImage) formData.append('cover_image', coverImage)
    if (coverBlobKey) formData.append('cover_image_blob_key', coverBlobKey)

    try {
      const { data } = await api.post('/admin/posts', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      if (data.success) {
        router.visit('/admin/posts')
      }
    } catch (err: unknown) {
      if (err && typeof err === 'object' && 'response' in err) {
        const response = (err as { response?: { data?: { errors?: string[] } } }).response
        if (response?.data?.errors) {
          setErrors({ base: response.data.errors.join(', ') })
        }
      }
    } finally {
      setSaving(false)
    }
  }

  return {
    title,
    slug,
    setSlug,
    excerpt,
    setExcerpt,
    content,
    setContent,
    status,
    setStatus,
    visibility,
    setVisibility,
    categoryId,
    setCategoryId,
    selectedTagIds,
    handleTagsChange,
    coverPreview,
    metaTitle,
    setMetaTitle,
    metaDescription,
    setMetaDescription,
    publishedAt,
    setPublishedAt,
    sticky,
    setSticky,
    saving,
    errors,
    mediaDialogOpen,
    setMediaDialogOpen,
    handleTitleChange,
    handleCoverChange,
    handleMediaSelect,
    removeCover,
    handleCancel,
    handleSubmit,
  }
}
